import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import './WeddingPhotosPage.css';

interface PhotoData {
  [category: string]: string[];
}

const categoryLabels: Record<string, string> = {
  bridal_party: 'Bridal Party',
  bride_and_groom: 'Bride & Groom',
  ceremony: 'Ceremony',
  dancing: 'Dancing',
  dinner: 'Dinner',
  friends_and_family: 'Friends & Family',
  engagement: 'Engagement',
};

const PhotoCategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const [photos, setPhotos] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  useEffect(() => {
    // Handle engagement photos separately
    if (category === 'engagement') {
      fetch('/engagement_photo_urls.json')
        .then((res) => {
          if (!res.ok) throw new Error('Failed to load engagement photo data');
          return res.json();
        })
        .then((data: string[]) => {
          if (data && data.length > 0) {
            setPhotos(data);
          } else {
            setError('No engagement photos found');
          }
          setLoading(false);
        })
        .catch((err) => {
          setError(err.message);
          setLoading(false);
        });
    } else {
      fetch('/wedding_photo_urls.json')
        .then((res) => {
          if (!res.ok) throw new Error('Failed to load photo data');
          return res.json();
        })
        .then((data: PhotoData) => {
          if (category && data[category]) {
            setPhotos(data[category]);
          } else {
            setError('Category not found');
          }
          setLoading(false);
        })
        .catch((err) => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, [category]);

  // Convert display URL to full-resolution download URL
  const getDownloadUrl = (displayUrl: string): string => {
    // Replace _display with empty string in the folder name
    return displayUrl.replace('_display/', '/');
  };

  // Get filename from URL for display
  const getFilename = (url: string): string => {
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  const getCategoryLabel = (cat: string): string => {
    return categoryLabels[cat] || cat.replace(/_/g, ' ');
  };

  const openPhoto = (url: string, index: number) => {
    setSelectedPhoto(url);
    setSelectedIndex(index);
  };

  const closePhoto = () => {
    setSelectedPhoto(null);
  };

  const goToPrevious = useCallback(() => {
    if (selectedIndex > 0) {
      const newIndex = selectedIndex - 1;
      setSelectedIndex(newIndex);
      setSelectedPhoto(photos[newIndex]);
    }
  }, [selectedIndex, photos]);

  const goToNext = useCallback(() => {
    if (selectedIndex < photos.length - 1) {
      const newIndex = selectedIndex + 1;
      setSelectedIndex(newIndex);
      setSelectedPhoto(photos[newIndex]);
    }
  }, [selectedIndex, photos]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedPhoto) return;

      if (e.key === 'Escape') {
        closePhoto();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhoto, goToPrevious, goToNext]);

  if (loading) {
    return (
      <main className="page photo-category-page">
        <div className="container">
          <p className="loading-text">Loading photos...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="page photo-category-page">
        <div className="container">
          <Link to="/photos" className="back-link">&larr; Back to Albums</Link>
          <p className="error-text">Error: {error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="page photo-category-page">
      <section className="photo-gallery-section">
        <div className="container">
          <Link to="/photos" className="back-link">&larr; Back to Albums</Link>

          <h1 className="section-title">{getCategoryLabel(category || '')}</h1>
          <p className="photos-subtitle">{photos.length} photos</p>

          <div className="photo-grid">
            {photos.map((url, idx) => (
              <div
                key={url}
                className="photo-item"
                onClick={() => openPhoto(url, idx)}
                style={{ animationDelay: `${Math.min(idx * 0.05, 0.8)}s` }}
              >
                <img
                  src={url}
                  alt={`Photo ${idx + 1}`}
                  className="photo-thumbnail"
                  loading="lazy"
                />
                <div className="photo-overlay">
                  <span className="photo-number">{idx + 1}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedPhoto && (
        <div
          className="lightbox-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePhoto();
          }}
        >
          <div className="lightbox-content">
            <button
              className="lightbox-close"
              onClick={closePhoto}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Navigation arrows */}
            {selectedIndex > 0 && (
              <button
                className="lightbox-nav lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                aria-label="Previous photo"
              >
                &#8249;
              </button>
            )}

            {selectedIndex < photos.length - 1 && (
              <button
                className="lightbox-nav lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                aria-label="Next photo"
              >
                &#8250;
              </button>
            )}

            <img
              src={selectedPhoto}
              alt={`Photo ${selectedIndex + 1}`}
              className="lightbox-image"
            />

            <div className="lightbox-footer">
              <span className="lightbox-counter">
                {selectedIndex + 1} / {photos.length}
              </span>
              <div className="download-buttons">
                <a
                  href={selectedPhoto}
                  download={getFilename(selectedPhoto)}
                  className="download-btn download-btn-secondary"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Medium
                </a>
                <a
                  href={getDownloadUrl(selectedPhoto)}
                  download={getFilename(selectedPhoto)}
                  className="download-btn"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Full Resolution
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default PhotoCategoryPage;
